/**
 * Shared Match Engine contract (ADR-032 Sport Module Architecture).
 * Each sport owns rules; platform only calls this lifecycle API.
 *
 * Engines are functional: match state is passed in and returned (React-friendly).
 * Serialized blobs always include engineId + schemaVersion for live-sync routing.
 */

/**
 * @typedef {'A'|'B'} Side
 *
 * @typedef {Object} MatchLiveView
 * @property {number} scoreA
 * @property {number} scoreB
 * @property {Side} serving
 * @property {Array<{ scoreA: number, scoreB: number, winner?: string }>} sets
 * @property {Side|null} winner
 * @property {boolean} endsSwapped
 * @property {string|null} [serveDisplay] — sport-specific serve line (e.g. pickleball 0-0-2)
 * @property {object|null} [extras] — sport-specific UI hints
 *
 * @typedef {Object} StartMatchConfig
 * @property {object} teamA
 * @property {object} teamB
 * @property {number} setsToWin
 * @property {number} [pointsToWin]
 * @property {boolean} [endsSwapped]
 * @property {Side} [firstServerSide]
 *
 * @typedef {Object} MatchEngine
 * @property {string} engineId
 * @property {string} scoringSystem
 * @property {(config: StartMatchConfig) => object} startMatch
 * @property {(match: object, config: StartMatchConfig) => object} startNextGame
 * @property {(match: object, side: Side) => object} scorePoint
 * @property {(match: object, faultType: string, side?: Side|null) => object} fault
 * @property {(match: object) => object} undo
 * @property {(match: object|null|undefined) => boolean} canUndo
 * @property {(match: object|null|undefined) => object|null} getState
 * @property {(match: object|null|undefined) => MatchLiveView} getLiveView
 * @property {(match: object|null|undefined) => Side|null} getWinner
 * @property {(match: object|null|undefined) => string|null} serialize
 * @property {(json: string|object|null|undefined) => object|null} deserialize
 * @property {(match: object|null|undefined) => boolean} isGamePoint
 * @property {(match: object|null|undefined) => boolean} isMatchPoint
 * @property {(match: object|null|undefined) => boolean} hasInterval
 * @property {(match: object, options?: { applyCourtChange?: boolean }) => object} resumeInterval
 * @property {(match: object|null|undefined) => boolean} isAwaitingCourtSetup
 * @property {(match: object|null|undefined) => object} suggestCourtSetup
 * @property {(teamA: object, teamB: object, match: object|null|undefined) => { teamA: object, teamB: object }} [syncTeamsToBoxes]
 * @property {() => Array<{ id: string, label: string }>} [listFaultTypes]
 */

/**
 * Wrap a sport match state for live-sync / persistence.
 * @param {string} engineId
 * @param {number} schemaVersion
 * @param {object} state
 */
export function wrapSerializedMatch(engineId, schemaVersion, state) {
  return JSON.stringify({
    engineId,
    schemaVersion,
    state,
  });
}

/**
 * @param {string|object|null|undefined} json
 * @returns {{ engineId: string|null, schemaVersion: number|null, state: object|null, legacy: boolean }}
 */
export function unwrapSerializedMatch(json) {
  if (!json) {
    return { engineId: null, schemaVersion: null, state: null, legacy: false };
  }
  try {
    const parsed = typeof json === "string" ? JSON.parse(json) : json;
    if (parsed && typeof parsed === "object" && parsed.state != null && parsed.engineId) {
      return {
        engineId: String(parsed.engineId),
        schemaVersion: Number(parsed.schemaVersion) || 1,
        state: parsed.state,
        legacy: false,
      };
    }
    // Legacy badminton blobs were raw Match JSON
    return {
      engineId: null,
      schemaVersion: null,
      state: parsed,
      legacy: true,
    };
  } catch {
    return { engineId: null, schemaVersion: null, state: null, legacy: false };
  }
}

/**
 * True when a live match has an in-progress scoring unit.
 * Rally sports use `activeGame`; tennis ITF uses `activeSet.game`.
 * @param {object|null|undefined} match
 * @returns {boolean}
 */
export function hasActiveScoringPlay(match) {
  return Boolean(match?.activeGame || match?.activeSet?.game);
}
